import React, { useMemo, useState } from 'react';

/**
 * Creator/storefront avatar component.
 *
 * Behavior:
 *   - If a real avatar URL is provided AND it loads → render the image.
 *   - On image error → fall back to a unique procedural avatar.
 *   - If no URL → render the procedural avatar directly.
 *
 * Procedural avatar = a 2-color gradient + the storefront initial,
 * colors deterministically derived from a hash of the store name so
 * every creator gets a distinct, branded avatar (no two creators look
 * the same), and the same creator always gets the same avatar across
 * page loads.
 */

const PALETTE = [
  // Curated cyan-leaning palette to keep brand consistency.
  // Avoids warm tones the founder has banned site-wide (amber/yellow).
  ['#00F0FF', '#0A85FF'],
  ['#5BD3FF', '#1B4FFF'],
  ['#00C2FF', '#7B2BFF'],
  ['#7CF7FF', '#0A1FA8'],
  ['#A0E9FF', '#3F2AFF'],
  ['#26F0FF', '#1A2A6C'],
  ['#83D7FF', '#003B73'],
  ['#0FCFFF', '#3D00E0'],
  ['#46E5FF', '#005ED9'],
  ['#9BF5FF', '#1A0C57'],
];

function _hash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export default function CreatorAvatar({
  storeName = '',
  avatarUrl = null,
  size = 'md',           // 'sm' | 'md' | 'lg' | 'xl' | number (px)
  className = '',
  testId = 'creator-avatar',
}) {
  const [imgFailed, setImgFailed] = useState(false);

  const { gradient, initial } = useMemo(() => {
    const name = (storeName || '?').trim();
    const h = _hash(name || 'fallback');
    const [from, to] = PALETTE[h % PALETTE.length];
    const init = name.charAt(0).toUpperCase() || '?';
    return { gradient: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`, initial: init };
  }, [storeName]);

  const sizeMap = { sm: 'w-10 h-10 text-base', md: 'w-16 h-16 text-2xl', lg: 'w-24 h-24 text-3xl', xl: 'w-32 h-32 md:w-40 md:h-40 text-5xl' };
  const sizeClass = typeof size === 'string' ? (sizeMap[size] || sizeMap.md) : '';
  const inlineSize = typeof size === 'number' ? { width: `${size}px`, height: `${size}px`, fontSize: `${Math.round(size * 0.4)}px` } : null;

  const showImage = avatarUrl && !imgFailed;

  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden ${sizeClass} ${className}`}
      style={inlineSize || undefined}
      data-testid={testId}
    >
      {showImage ? (
        <img
          src={avatarUrl}
          alt={storeName || 'Storefront avatar'}
          className="w-full h-full object-cover"
          onError={() => setImgFailed(true)}
          loading="lazy"
        />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center font-black text-white"
          style={{ background: gradient }}
          data-testid={`${testId}-fallback`}
        >
          {initial}
        </div>
      )}
    </div>
  );
}
